import { FooterLink, parseBroadcastQuality, SiteSettings } from '@core/shared';
import { createIsomorphicFn } from '@tanstack/react-start';
import { convertToValidJson } from './convertToValidJson';

const SITE_SETTINGS_ID = 'siteSettings';
const injectSiteSettings = `window.${SITE_SETTINGS_ID} = JSON.parse(document.getElementById('${SITE_SETTINGS_ID}').textContent)`;

export const parseSiteSettings = () => {
    // Parse site settings only once on the server side and pass them to the client using the "beforeLoad" from the router context.
    let footerLinks: FooterLink[] = [];
    try {
        if (process.env.VITE_SITE_FOOTER_LINKS) {
            footerLinks = JSON.parse(convertToValidJson(process.env.VITE_SITE_FOOTER_LINKS)) as FooterLink[];
        }
    } catch (error) {
        console.error('Error parsing footer links:', {
            footerLinks: process.env.VITE_SITE_FOOTER_LINKS,
            error
        });
    }

    const siteSettings = {
        isWhitelabel: process.env.VITE_IS_WHITELABEL === 'true',
        isProduction: process.env.VITE_IS_PRODUCTION === 'true',
        title: process.env.VITE_SITE_TITLE!,
        code: process.env.VITE_SITE_CODE!,
        defaultLanguage: process.env.VITE_SITE_DEFAULT_LANGUAGE!,
        defaultBroadcastQuality: parseBroadcastQuality(process.env.VITE_SITE_DEFAULT_BROADCAST_QUALITY),
        logoUrl: process.env.VITE_SITE_LOGO_URL ?? undefined,
        // || rather than ??: start.sh exports an unset setting as an empty string
        logoText: process.env.VITE_SITE_LOGO_TEXT || process.env.VITE_SITE_TITLE!,
        countdownImageUrl: process.env.VITE_SITE_COUNTDOWN_IMAGE_URL ?? undefined,
        countdownVideoUrl: process.env.VITE_SITE_COUNTDOWN_VIDEO_URL ?? undefined,
        footerLinks: footerLinks,
        baseUrl: process.env.VITE_WEBINAR_API!
    } as SiteSettings;
    return siteSettings;
};

export const getSiteSettingsScript = createIsomorphicFn()
    .server(() => {
        const siteSettings = parseSiteSettings();
        const json = JSON.stringify(siteSettings);
        return [
            {
                type: 'application/json',
                id: SITE_SETTINGS_ID,
                children: json
            },
            {
                children: injectSiteSettings
            }
        ];
    })
    .client(() => []);

let cachedSiteSettings: SiteSettings | null = null;
export const getSiteSettings = createIsomorphicFn()
    .server(() => {
        cachedSiteSettings ||= parseSiteSettings();
        return cachedSiteSettings || ({} as SiteSettings);
    })
    .client(() => {
        cachedSiteSettings ||= globalThis.document.getElementById(SITE_SETTINGS_ID)?.textContent
            ? (JSON.parse(document.getElementById(SITE_SETTINGS_ID)!.textContent!) as SiteSettings)
            : null;

        return cachedSiteSettings || ({} as SiteSettings);
    });

// The backend as this side reaches it: the server over the cluster-internal address, the browser
// over the public one. Server-only, so deliberately not part of the serialised site settings.
export const getBackendOrigin = createIsomorphicFn()
    .server(() => process.env.VITE_WEBINAR_SERVER_API!)
    .client(() => getSiteSettings().baseUrl);
